import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SectionForm from '../components/Forms/SectionForm';
import { apiClient } from '../lib/api';
import type { Section } from '../lib/api';

const SectionEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [section, setSection] = useState<Section | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = id !== 'new';

  useEffect(() => {
    if (isEditing && id) {
      fetchSection(id);
    }
  }, [id, isEditing]);

  const fetchSection = async (sectionId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const sectionData = await apiClient.getSection(sectionId);
      setSection(sectionData);
    } catch (error: any) {
      console.error('Failed to fetch section:', error);
      setError(error.message || 'Failed to load section');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (sectionData: Partial<Section>) => {
    try {
      setError(null);
      
      if (isEditing && id) {
        // Update existing section
        console.log('Updating section:', { id, ...sectionData });
        await apiClient.updateSection(id, sectionData);
        
        // Update local state
        if (section) {
          setSection(prev => prev ? { ...prev, ...sectionData } as Section : null);
        }
      } else {
        // Create new section
        console.log('Creating section:', sectionData);
        const response = await apiClient.createSection(sectionData);
        console.log('Section created:', response);
        
        // For new sections, navigate to edit page instead of back to list
        // This allows users to immediately upload images
        const newSectionId = response.id;
        if (newSectionId) {
          navigate(`/sections/${newSectionId}/edit`, { 
            state: { 
              message: 'Section created successfully! You can now upload images.' 
            }
          });
          return; // Exit early to prevent the navigation below
        }
      }
      
      // Only navigate back to sections list for updates
      if (isEditing) {
        navigate('/sections', { 
          state: { 
            message: 'Section updated successfully' 
          }
        });
      }
      
    } catch (error: any) {
      console.error('Save error:', error);
      throw error; // Re-throw to let the form handle it
    }
  };

  const handleCancel = () => {
    navigate('/sections');
  };

  const handleDelete = async (sectionId: string) => {
    try {
      // For development - just simulate success
      console.log('Would delete section:', sectionId);
      
      // In production, this would call:
      // await apiClient.deleteSection(sectionId);
      
      navigate('/sections', { 
        state: { 
          message: 'Section deleted successfully' 
        }
      });
    } catch (error: any) {
      console.error('Delete error:', error);
      throw error; // Re-throw to let the form handle it
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="admin-card">
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-gray-600">Loading section...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="admin-card text-center py-12">
          <div className="text-error text-4xl mb-4">⚠️</div>
          <h2 className="text-lg font-medium text-gray-9 mb-2">Failed to Load Section</h2>
          <p className="text-gray-6 mb-4">{error}</p>
          <div className="flex items-center justify-center space-x-3">
            <button
              onClick={() => id && fetchSection(id)}
              className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
            >
              Retry
            </button>
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Back to Sections
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SectionForm
      section={isEditing ? section : null}
      onSave={handleSave}
      onCancel={handleCancel}
      onDelete={isEditing ? handleDelete : undefined}
      isLoading={isLoading}
    />
  );
};

export default SectionEditor;